/* eslint-disable react/require-default-props */
import React from 'react';
import proptypes from 'prop-types';

import { extend } from '@thd-nucleus/data-sources';
import { ConsumerCardMessaging } from '@thd-olt-component-react/consumer-card-messaging';

import { getCustomerType } from '../utils/product-bundle-utils';
import { getBundleWithPricing } from '../utils/bundle-price-utils';
import { isBundleUnavailable } from '../utils/product-unavailable-utils';

import { CustomSkeletonLine } from './subcomponents/CustomSkeletonLine';
import useGetPlaceholderHeight from '../hooks/useGetPlaceholderHeight';

const { string, bool, shape, arrayOf } = proptypes;
/**
 * Component for rendering messaging related to a bundle consumer card.
 *
 * @param {Object} props - The props for the component.
 * @param {Object} props.product - The product data, including features and other details.
 * @param {Object} props.clsRemediation - The CLS remediation data.
 * @param {Object} props.pricingDetails - The pricing details for the product.
 * @param {boolean} props.pricingDetails.loading - Indicates if the pricing details are still loading.
 * @param {Object} props.pricingDetails.pricing - The pricing information for the product.
 *
 * @returns {JSX.Element|null} The rendered component or null if pricing details are loading.
 */
const BundleConsumerCardMessaging = ({
  product,
  products,
  clsRemediation = { placeholders: false, preservePlaceholders: false },
  pricingDetails = null
}) => {
  const { productType, hidePlccCard } = product?.features ?? {};
  const isUnavailable = hidePlccCard && isBundleUnavailable({ products });
  const { consumerCardPlaceholderHeight } = useGetPlaceholderHeight(product);
  if (isUnavailable) {
    return null;
  }
  if (pricingDetails?.loading) {
    return (
      <CustomSkeletonLine height={consumerCardPlaceholderHeight} />
    );
  }

  const bundleWithPricing = getBundleWithPricing({ bundle: product, pricing: pricingDetails?.pricing });

  // MIGRATION FIX (Webpack→Vitest): Fixed data structure for ConsumerCardMessaging component.
  // Issue: ConsumerCardMessaging requires product.identifiers AND product.pricing to render.
  // Detection: Tests were failing with empty DOM - component validation was preventing render.
  // Previous buggy line: identifiers: { ...bundleWithPricing, productType }
  // This was incorrectly spreading the entire bundleWithPricing object into identifiers,
  // corrupting the data structure. Now properly merges identifiers from bundleWithPricing.
  const productData = {
    ...bundleWithPricing,
    identifiers: {
      ...bundleWithPricing?.identifiers,
      productType
    }
  };

  return (
    <div data-component="BundleConsumerCardMessaging" data-testid="bundle-consumer-card-messaging">
      <ConsumerCardMessaging
        product={productData}
        isB2B={getCustomerType() === 'B2B'}
        clsRemediation={clsRemediation}
        // MIGRATION FIX (Webpack→Vitest): Added missing campaign codes.
        paymentEstimatorCampaignCode="~P~1~J~4~B~ZZZ0~ML~HD~ZZ"
        applyNowCampaignCode="~P~1~J~4~B~ZZZ0~ML~HD~ZZ"
      />
    </div>
  );
};

BundleConsumerCardMessaging.propTypes = {
  product: shape({
    bundleSpecificationDetails: shape({
      type: string
    })
  }).isRequired,
  pricingDetails: shape({
    pricing: shape({}),
    loading: bool
  }),
  products: arrayOf(
    shape({ })
  ).isRequired,
  clsRemediation: shape({
    placeholders: bool,
    preservePlaceholders: bool
  })
};

BundleConsumerCardMessaging.displayName = 'BundleConsumerCardMessaging';

BundleConsumerCardMessaging.dataModel = extend(ConsumerCardMessaging);

export { BundleConsumerCardMessaging };